import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePriceGroup } from 'core/contexts/priceGroup';
import { UnitType } from '@utriper/core';
import arrayMutators from 'final-form-arrays';
import { Form } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { Box, Button, Checkbox, FormControlLabel, FormGroup, FormHelperText, Grid2 } from '@mui/material';
import { Add, RadioButtonChecked, RadioButtonUnchecked } from '@mui/icons-material';
import { AlertDialog, SelectComponent } from '@utriper/core-ui';
import Title from '../../../components/Title';
import ActionButtons from '../ActionButtons';
import AddUnitForm from '../AddUnitForm';

// REVIEW
const getUnitEmoji = (unitType) => {
    switch (unitType) {
        case UnitType.IDS.adult:
            return '🧑';
        case UnitType.IDS.youth:
            return '🧒';
        case UnitType.IDS.child:
            return '👦';
        case UnitType.IDS.infant:
            return '👶';
        case UnitType.IDS.family:
            return '👨‍👩‍👧‍👦';
        case UnitType.IDS.senior:
            return '👴';
        case UnitType.IDS.student:
            return '🎓';
        case UnitType.IDS.military:
            return '🎖️';
        case UnitType.IDS.other:
            return '🧍';
        default:
            return null;
    }
};

const UnitsForm = () => {
    const { i18n, t } = useTranslation();
    const translate = (key) => t(`price_group_wizard.units_form.${key}`);
    const { units, unitsOptions, unitsForm, handleUpdateUnits, handleNext, handleBack } = usePriceGroup();
    const [submitted, setSubmitted] = useState(false);
    const [open, setOpen] = useState(false);

    const validateForm = (values) => {
        const errors = {};
        if (!Array.isArray(values?.unitsIds) || values?.unitsIds?.length === 0) {
            errors.unitsIds = translate('validate_form.missing_units');
            return errors;
        }

        const unitsErrors = values.unitsIds.map((unit) => {
            if (!unit?.unitId) {
                return { typeId: unit.typeId, error: translate('validate_form.missing_id') };
            }

            return null;
        });

        if (unitsErrors.some(Boolean)) {
            errors.unitsIds = unitsErrors.filter(Boolean);
        }

        return errors;
    };

    const handleSubmit = (values) => {
        handleUpdateUnits(values);
        handleNext();
    };

    return (
        <>
            <Form
                onSubmit={handleSubmit}
                mutators={{ ...arrayMutators }}
                initialValues={unitsForm}
                validate={validateForm}
                render={({ errors, handleSubmit }) => {
                    const { unitsIds: unitsErrors } = errors;

                    let hasSelectError = false;

                    if (Array.isArray(unitsErrors)) {
                        hasSelectError = true;
                    }

                    const hasError = submitted && !hasSelectError && unitsErrors;

                    return (
                        <form onSubmit={handleSubmit} noValidate style={{ width: '100%', maxWidth: '480px' }}>
                            <Box component="section">
                                <Title title={translate('title')} description={translate('description')} />

                                {/* Form */}
                                <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" mt="24px">
                                    <FieldArray name="unitsIds">
                                        {({ fields }) => (
                                            <FormGroup sx={{ width: '100%' }}>
                                                <Box sx={{ maxHeight: 320, overflowY: 'auto', width: '100%', mb: 1, display: 'flex', flexDirection: 'column' }}>
                                                    {unitsOptions?.map((unit) => {
                                                        const translatedType = UnitType.getTranslatedName(UnitType.types, unit.typeId, i18n.language);
                                                        const checkboxLabel = `${getUnitEmoji(unit?.typeId)} ${translatedType}`;
                                                        const selectLabel = `${translate('select_label')} ${translatedType}`;
                                                        if (!Array.isArray(fields?.value)) {
                                                            fields.value = [];
                                                        }
                                                        const existingIndex = fields?.value?.findIndex((item) => item.typeId === unit?.typeId);
                                                        const isChecked = existingIndex !== -1;

                                                        const handleCheck = () => {
                                                            const defaultValue = unit?.values?.length === 1 ? unit.values[0]?.id : null;

                                                            fields.push({
                                                                typeId: unit?.typeId,
                                                                unitId: defaultValue,
                                                            });
                                                        };

                                                        const removeCheck = () => {
                                                            fields.remove(existingIndex);
                                                        };

                                                        const handleClick = (event) => {
                                                            const isFromSelect = event?.target?.closest('.MuiTypography-root') || event?.target?.closest('.MuiList-root') || event?.target?.closest('.MuiBackdrop-root') || event?.target?.closest('.MuiPopover-root'); // REVIEW
                                                            if (isFromSelect) return;
                                                            event?.preventDefault();

                                                            if (!isChecked) {
                                                                handleCheck();
                                                            } else if (isChecked) {
                                                                removeCheck();
                                                            }
                                                        };

                                                        const handleCheckboxChange = (event) => {
                                                            if (event?.target?.checked) {
                                                                handleCheck();
                                                            } else if (isChecked) {
                                                                removeCheck();
                                                            }
                                                        };

                                                        const handleSelectChange = (value) => {
                                                            fields.update(existingIndex, {
                                                                typeId: unit?.typeId,
                                                                unitId: value?.id,
                                                            });
                                                        };
                                                        const selectedUnitId = isChecked && fields?.value[existingIndex]?.unitId;

                                                        let selectError = null;

                                                        if (hasSelectError) {
                                                            selectError = unitsErrors?.find((error) => error?.typeId === unit?.typeId)?.error;
                                                        }

                                                        return (
                                                            <Button // TODO: IMPROVE THIS
                                                                key={unit?.typeId}
                                                                sx={{
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    justifyContent: 'flex-start',
                                                                    alignItems: 'flex-start',
                                                                    mb: 2,
                                                                    width: '100%',
                                                                    padding: '12px 8px',
                                                                    border: `solid ${isChecked ? '2px var(--color-utriper-blue)' : '1px #13131340'}`,
                                                                    borderRadius: '8px',
                                                                    boxSizing: 'border-box',
                                                                    textTransform: 'none',
                                                                }}
                                                                onClick={handleClick}
                                                            >
                                                                <FormControlLabel
                                                                    control={<Checkbox checked={isChecked} onChange={handleCheckboxChange} name={checkboxLabel} icon={<RadioButtonUnchecked sx={{ color: 'var(--color-utriper-blue)' }} />} checkedIcon={<RadioButtonChecked sx={{ color: 'var(--color-utriper-blue)' }} />} />}
                                                                    label={checkboxLabel}
                                                                    sx={{
                                                                        '& .MuiTypography-root': {
                                                                            fontSize: '18px',
                                                                            color: '#131313',
                                                                        },
                                                                    }}
                                                                />

                                                                {isChecked && unit?.values?.length > 1 && (
                                                                    <Grid2 container width="100%" pl={4}>
                                                                        <SelectComponent value={selectedUnitId} onChange={handleSelectChange} values={unit.values} style={{ width: '100%' }} label={selectLabel} hasError={submitted && selectError} errorMessage={selectError} />
                                                                    </Grid2>
                                                                )}
                                                            </Button>
                                                        );
                                                    })}
                                                </Box>

                                                {hasError && <FormHelperText error={hasError}>{unitsErrors}</FormHelperText>}

                                                {units?.length !== unitsOptions?.length && units?.length > 3 && (
                                                    <Box display="flex" justifyContent="flex-start" alignItems="center" mt={2}>
                                                        <Button sx={{ color: 'var(--color-utriper-blue)', textTransform: 'none' }} variant="text" startIcon={<Add />} onClick={() => setOpen(true)}>
                                                            {translate('add_button')}
                                                        </Button>
                                                    </Box>
                                                )}
                                            </FormGroup>
                                        )}
                                    </FieldArray>
                                </Grid2>

                                <ActionButtons
                                    handleSubmit={(e) => {
                                        setSubmitted(true);
                                        return handleSubmit(e);
                                    }}
                                    handleBack={handleBack}
                                />
                            </Box>
                        </form>
                    );
                }}
            />

            {/* Add Unit Type Form */}
            <AlertDialog open={open} handleClose={() => setOpen(false)} hideTitle hideActions maxWidth="sm" fullWidth dialogContent={<AddUnitForm handleClose={() => setOpen(false)} />} />
        </>
    );
};

export default UnitsForm;
